import { Component, Fragment } from "inferno";
import { Card, CardBody, CardText } from "inferno-bootstrap";
import { Entity, IChatMessage } from "../../../../utils/types";
import { AppState, Configuration } from "../../../../services/configuration";
import { Channel, ChatMessageFormat, Event, EventBusInstance, Log } from "@sap/cpaas-commlib-lib";
import { CommlibService } from "../../../../services/commlibService";
import Error from "inferno-feather/dist/icons/x-circle";
import { T } from "inferno-i18next";
import throttle from "lodash/throttle";
import { TextBody } from "./TextBody";

type TemplateContent = {
	text: string,
	template: {
		id: string, template_type: "response" | "request" | "static_request", template_id?: string, response?: string, response_html?: string, cancelled?: boolean, variables?: { [id: string]: string }
	}
};

type Props = { content: TemplateContent, entity: Entity, alias: string };
type State = { error: boolean, busy: boolean };

interface TemplateWindow extends Window {
	onChatMessage: (msg: string) => void;
	resolveTemplate: (msg: string, html: string) => void;
	rejectTemplate: () => void;
	onBodyLoad: () => void;
	init: (content: TemplateContent) => void;
}

interface TemplateFrame extends HTMLIFrameElement {
	contentWindow: TemplateWindow;
}

export class TemplateBody extends Component<Props, State> {

	frameRef: TemplateFrame;
	frameContent: string;
	responseSent: boolean;
	log: any;

	/**
	 * Default template variables. Visitor only receives agent alias, so USER values are wrong...
	 * @see https://help.sap.com/viewer/7d61aa223fdc4f3189c102fb80d9388b/1911/en-US/01f223b729544fb2ab1e343a0e7424cc.html
	 */
	defaultReplacements: { [key: string]: string };

	state: State = {
		error: false,
		busy: false,
	};

	constructor(props: any) {
		super(props);
		this.log = new Log("TemplateBody" + (props.content.template_id || ""));
		this.defaultReplacements = {
			"{CUSTOMERNAME}": Configuration.userName,
			"{USERFIRSTNAME}": props.alias,
			"{USERLASTNAME}": props.alias,
			"{USERNAME}": props.alias
		};
		if (!props.content || props.content.response === "parseError") {
			this.state.error = true;
			this.log.wrn("c", "Parse failed");
		} else {
			this.parseTemplateMessage();
		}
	}

	// shouldComponentUpdate(nextProps: Props, nextState: State, context: any): boolean {
	// 	// only allow rendering if there was an error, otherwise we might lose the iframe
	// 	// return nextState.error;
	// }

	componentDidMount() {
		EventBusInstance.subscribe(Configuration.sizeEvent, this.sizeChange, this, Configuration.component);
		EventBusInstance.subscribe(Event.Chat(Channel.all).message, this.onChatMessage, this);
	}

	componentWillMount() {
		EventBusInstance.unsubscribe(Configuration.sizeEvent, this.sizeChange, this, Configuration.component);
		EventBusInstance.unsubscribe(Event.Chat(Channel.all).message, this.onChatMessage, this);
	}

	sizeChange = throttle(() => {
		const body = this.frameRef?.contentWindow?.document?.body;
		if (body) {
			this.frameRef.width = body.scrollWidth + "px";
			this.frameRef.height = body.scrollHeight + "px";
		}
	}, 200);

	onChatMessage(channel: string, event: string, message: IChatMessage) {
		// pass the message to template to see if it want's to resolve
		if (message.format === ChatMessageFormat.text && !this.responseSent && this.frameRef?.contentWindow?.onChatMessage && this.props.content.template.template_type === "request" && message.sender === AppState.activeLeg.getId()) {
			try {
				this.frameRef.contentWindow.onChatMessage(message.content);
			} catch (e) {
				this.log.wrn("onChatMessage", "Template script failed to process message");
			}
		}
	}

	async parseTemplateMessage() {
		const template = this.props.content.template;
		if (template.template_type === "request" && template.response === undefined) {
			let body;
			try {
				// request with no resp => load template content from server
				body = (await CommlibService.fetchTemplate(template.template_id)).body;
			} catch (e) {
				this.setState({ error: true });
				this.log.wrn("parseTemplateMessage", "template does not exits");
				return;
			}
			this.frameContent = body;
		} else if (template.template_type === "static_request") {
			let body;
			try {
				body = (await CommlibService.fetchStaticTemplate(template.template_id)).body;
			} catch (e) {
				this.setState({ error: true });
				this.log.wrn("parseTemplateMessage", "template does not exits");
				return;
			}
			this.frameContent = body;
		} else {
			this.frameContent = template.response_html ?? template.response;
		}
		// replace default template variables
		for (const key in this.defaultReplacements) {
			try {
				this.frameContent = this.frameContent.replace(new RegExp(`/${key}/gm`), this.defaultReplacements[key]);
			} catch (e) {
				// not relevant
			}
		}
		this.log.dbg("parseTemplateMessage", "template loaded");
		this.setFrameContent();
	}

	setContentRef = (node: any) => {
		if (node?.contentWindow?.document) {
			this.frameRef = node;
			this.setFrameContent();
		}
	};

	setFrameContent() {
		const template = this.props.content?.template;
		const content = template?.response_html || template?.response || this.frameContent;
		if (this.frameRef && content) {
			this.log.dbg("setFrameContent");
			const doc = this.frameRef.contentWindow.document;
			let styles: string;
			if (process.env.NODE_ENV === "development") {
				styles = `<link rel="stylesheet" href="resources/css/bootstrap.css"</link>`;
			} else {
				const head = Array.from(document.head.children || []);
				const styleRef: any = head.find((child: any) => child.rel === "stylesheet" && child.href.indexOf("styles.css") !== -1);
				const customStyleRef = head.find((child: any) => child.id === "styles");
				styles = `<link rel="stylesheet" href="${styleRef.href}"</link><style>${customStyleRef.innerHTML}</style>`;
			}
			// Poll for frame loaded
			let intervalCount = 0;
			let interval = setInterval(() => {
				// IE does not allow inserting window functions before load => sniff the window variable cpLoaded until iframe loaded
				intervalCount++;
				if ((this.frameRef.contentWindow as any).cpLoaded === true) {
					// Add template variables to the iframe
					clearInterval(interval);
					for (const key in (template?.variables || {})) {
						(this.frameRef.contentWindow as any)[key] = template.variables[key];
					}
					this.frameRef.contentWindow.resolveTemplate = (msg, html) => this.resolve(msg, html, false);
					this.frameRef.contentWindow.rejectTemplate = () => this.resolve(undefined, undefined, true);
					if (this.frameRef.contentWindow.init) {
						try {
							this.frameRef.contentWindow.init(this.props.content);
						} catch (e) {
							this.log.wrn("setFrameContent", "template init script");
						}
					}
					this.frameRef.width = doc.body.scrollWidth + "px";
					this.frameRef.height = doc.body.scrollHeight + "px";
					this.log.dbg("setFrameContent", "template loaded");
				} else if (intervalCount > 60) {
					// failed insert callbacks
					this.log.wrn("setFrameContent", "load failed");
					this.setState({ error: true });
					clearInterval(interval);
				}
			}, 100);
			doc.open("text/html", "replace");
			doc.write(`<!DOCTYPE html><html><head>${styles}<script>function onLoad(){window.cpLoaded=true}</script></head><body onload="onLoad()" style="overflow:hidden;width: ${template.template_type === "response" ? "max-content" : "auto"}"><div style="display: flex">${content}</div></body></html>`);
			doc.close();
		}
	}

	resolve = async (content: string, html: string, reject?: boolean) => {
		const resp: TemplateContent = {
			text: "",
			template: {
				response: content,
				response_html: html ? encodeURI(html) : undefined,
				template_type: "response",
				id: this.props.content.template.id,
				cancelled: reject ? true : undefined,
				template_id: this.props.content.template.template_id
			}
		};
		this.responseSent = true;
		try {
			await AppState.activeLeg.sendMessage(JSON.stringify(resp), ChatMessageFormat.template);
		} catch (e) {
			this.log.wrn("resolve", "failed to respond", e);
			this.setState({ error: true });
		}
	};

	render() {
		if (this.state.error) {
			return (
				<Card className="m-1 ml-5 rounded">
					<CardBody>
						<Error className="text-danger mr-2 d-inline" />
						<CardText className="d-inline"><T className="d-inline" i18nKey="VCC_XMSG_TEMPLATE_FAIL">x</T></CardText>
					</CardBody>
				</Card>
			);
		} else if (this.props.content.template.cancelled) {
			// don't show cancelled templates 
			return null;
		} else if (this.props.entity === Entity.visitor) {
			return (
				<Card className="m-1 ml-5 v-bubble border-primary">
					<CardBody className="p-2">
						<iframe ref={this.setContentRef} frameBorder={0} height={0} width={0} />
					</CardBody>
				</Card>
			);
		} else {
			return (
				<Fragment>
					{this.props.content.text && (<TextBody content={this.props.content.text} html={true} entity={Entity.agent} />)}
					<Card className="m-1" style={{ "flex-grow": 1, border: 0 }}>
						<iframe ref={this.setContentRef} frameBorder={0} style={{ width: "100%" }} />
					</Card>
				</Fragment>
			);
		}
	}
}
